import dayjs from '~/timeInit';

import { isValidDateTimeInput } from '../dateTime';
import { isSiteRegionCode } from '../regions';
import type { PayloadValidationResult } from '../types';
import { isHttpsUrl, isInternalRoute } from '../url';
import { validateRequiredString } from '../validationHelpers';

import type { SpendPointsPayloadBuilderState, SpendPointsWaysToBoostTile } from './types';

export type SpendPointsValidationResult = PayloadValidationResult;

function validationLabel(sectionLabel: string, fieldLabel: string): string {
  return `${sectionLabel} ${fieldLabel}`;
}

function validateCtaUrl(value: string, label: string, errors: Array<string>) {
  const ctaUrlLabel = validationLabel(label, 'CTA URL');
  validateRequiredString(value, ctaUrlLabel, errors);
  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return;
  }
  if (isHttpsUrl(trimmedValue) || isInternalRoute(trimmedValue)) {
    return;
  }
  errors.push(`${ctaUrlLabel} must be either an https:// URL or an internal route starting with /`);
}

function validateEligibleRegions(regions: Array<string>, label: string, errors: Array<string>) {
  const invalidRegions = regions.filter((region) => !isSiteRegionCode(region));
  if (invalidRegions.length > 0) {
    errors.push(`${validationLabel(label, 'Eligible Regions')} has invalid codes: ${invalidRegions.join(', ')}`);
  }
}

function validateDateRange(startAt: string, endAt: string, label: string, errors: Array<string>) {
  const isStartAtValid = isValidDateTimeInput(startAt);
  const isEndAtValid = isValidDateTimeInput(endAt);

  if (!isStartAtValid) {
    errors.push(`${validationLabel(label, 'Start at')} must be a valid datetime`);
  }
  if (!isEndAtValid) {
    errors.push(`${validationLabel(label, 'End at')} must be a valid datetime`);
  }
  if (!isStartAtValid || !isEndAtValid) {
    return;
  }

  if (dayjs(endAt).isBefore(dayjs(startAt))) {
    errors.push(`${validationLabel(label, 'End at')} must be the same as or after Start at`);
  }
}

function validateTile(tile: SpendPointsWaysToBoostTile, errors: Array<string>) {
  const label = tile.draftLabel;
  validateEligibleRegions(tile.enabledRegions, label, errors);
  validateDateRange(tile.startAt, tile.endAt, label, errors);
  validateRequiredString(tile.imageId, validationLabel(label, 'Image ID'), errors);
  validateRequiredString(tile.title, validationLabel(label, 'Title'), errors);
  validateRequiredString(tile.description, validationLabel(label, 'Description'), errors);
  validateRequiredString(tile.ctaLabel, validationLabel(label, 'CTA label'), errors);
  validateCtaUrl(tile.ctaUrl, label, errors);
}

export function validateSpendPointsPayload(state: SpendPointsPayloadBuilderState): SpendPointsValidationResult {
  const errors: Array<string> = [];

  if (state.waysToBoostTiles.length === 0) {
    errors.push('Ways to Boost tiles requires at least one tile');
  }

  state.waysToBoostTiles.forEach((tile) => validateTile(tile, errors));

  return {
    isValid: errors.length === 0,
    errors,
  };
}
